import { proApiFetch, authenticatedProApiFetch } from "./pro-api.client";

export type LocalizationContext = "shared" | "pro" | "certification" | "account";

export interface Language {
  id: string;
  code: string;
  name: string;
  description: string | null;
  flagUrl: string | null;
  enabled: boolean;
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface LocalizationModulePayload {
  unchanged: boolean;
  version: number;
  languageCode: string;
  context: string;
  definitions?: Record<string, string>;
  updatedAt?: string;
}

export interface LocalizationModuleAdmin {
  id: string;
  languageCode: string;
  context: string;
  version: number;
  definitions: Record<string, string>;
  updatedAt: string;
  createdAt: string;
}

export async function fetchEnabledLanguages(): Promise<Language[]> {
  return proApiFetch<Language[]>("/localization/languages", { method: "GET" });
}

export async function fetchAdminLanguages(accessToken: string): Promise<Language[]> {
  return authenticatedProApiFetch<Language[]>(
    "/localization/languages/admin",
    accessToken,
    { method: "GET" },
  );
}

export async function createLanguage(
  accessToken: string,
  data: {
    code: string;
    name: string;
    description?: string | null;
    flagUrl?: string | null;
    enabled?: boolean;
    isDefault?: boolean;
  },
): Promise<Language> {
  return authenticatedProApiFetch<Language>("/localization/languages", accessToken, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateLanguage(
  accessToken: string,
  id: string,
  data: {
    name?: string;
    description?: string | null;
    flagUrl?: string | null;
    enabled?: boolean;
    isDefault?: boolean;
  },
): Promise<Language> {
  return authenticatedProApiFetch<Language>(
    `/localization/languages/${id}`,
    accessToken,
    { method: "PATCH", body: JSON.stringify(data) },
  );
}

export async function deleteLanguage(accessToken: string, id: string): Promise<void> {
  await authenticatedProApiFetch(`/localization/languages/${id}`, accessToken, {
    method: "DELETE",
  });
}

export async function fetchLocalizationModule(
  context: LocalizationContext,
  lang: string,
  sinceVersion?: number,
): Promise<LocalizationModulePayload> {
  const params = new URLSearchParams({ lang });
  if (sinceVersion != null) params.set("sinceVersion", String(sinceVersion));
  return proApiFetch<LocalizationModulePayload>(
    `/localization/modules/${encodeURIComponent(context)}?${params}`,
    { method: "GET" },
  );
}

export async function fetchModulesForLanguage(
  accessToken: string,
  lang: string,
): Promise<LocalizationModuleAdmin[]> {
  return authenticatedProApiFetch<LocalizationModuleAdmin[]>(
    `/localization/modules?lang=${encodeURIComponent(lang)}`,
    accessToken,
    { method: "GET" },
  );
}

export async function putLocalizationModule(
  accessToken: string,
  context: LocalizationContext,
  lang: string,
  definitions: Record<string, string>,
): Promise<LocalizationModulePayload> {
  return authenticatedProApiFetch<LocalizationModulePayload>(
    `/localization/modules/${encodeURIComponent(context)}?lang=${encodeURIComponent(lang)}`,
    accessToken,
    { method: "PUT", body: JSON.stringify({ definitions }) },
  );
}

export async function patchLocalizationModuleDefinitions(
  accessToken: string,
  context: LocalizationContext,
  lang: string,
  patch: { set?: Record<string, string>; delete?: string[] },
): Promise<LocalizationModuleAdmin> {
  return authenticatedProApiFetch<LocalizationModuleAdmin>(
    `/localization/modules/${encodeURIComponent(context)}/definitions?lang=${encodeURIComponent(lang)}`,
    accessToken,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
}

export async function batchPatchLocalizationDefinitions(
  accessToken: string,
  body: {
    context: LocalizationContext;
    updates: Array<{
      lang: string;
      set?: Record<string, string>;
      delete?: string[];
    }>;
  },
): Promise<{ results: LocalizationModuleAdmin[] }> {
  return authenticatedProApiFetch<{ results: LocalizationModuleAdmin[] }>(
    "/localization/definitions/batch",
    accessToken,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export interface FlagUploadInstructions {
  uploadUrl: string;
  /** Shared avatar-storage response shape (reused for language flags). */
  avatarUrl: string;
  key: string;
  contentType?: string;
  expiresInSeconds?: number;
}

export async function createFlagUploadUrl(
  accessToken: string,
  contentType: string,
): Promise<FlagUploadInstructions> {
  return authenticatedProApiFetch<FlagUploadInstructions>(
    "/localization/languages/flag/upload-url",
    accessToken,
    { method: "POST", body: JSON.stringify({ contentType }) },
  );
}

/** Upload a language flag image; returns the public URL to store on the language. */
export async function uploadLanguageFlag(
  accessToken: string,
  file: File,
): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }
  const contentType = file.type || "image/png";
  const instructions = await createFlagUploadUrl(accessToken, contentType);
  const isPresigned = instructions.uploadUrl.includes("X-Amz-Signature");
  const headers: Record<string, string> = { "Content-Type": contentType };
  if (!isPresigned) {
    if (instructions.key) headers["X-Avatar-Key"] = instructions.key;
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  }

  const isAbsolute = /^https?:\/\//i.test(instructions.uploadUrl);
  if (!isAbsolute || instructions.uploadUrl.includes("/localization/")) {
    const path =
      instructions.uploadUrl.replace(/^https?:\/\/[^/]+/, "").replace(/^\/api/, "") ||
      "/localization/languages/flag/file";
    const saved = await authenticatedProApiFetch<{ avatarUrl?: string }>(
      path.startsWith("/") ? path : `/${path}`,
      accessToken,
      {
        method: "PUT",
        headers: {
          "Content-Type": contentType,
          "X-Avatar-Key": instructions.key,
        },
        body: file,
      },
    );
    return saved.avatarUrl || instructions.avatarUrl;
  }

  const res = await fetch(instructions.uploadUrl, {
    method: "PUT",
    body: file,
    headers,
  });
  if (!res.ok) throw new Error("Failed to upload flag image");
  if (res.headers.get("content-type")?.includes("application/json")) {
    const body = (await res.json()) as { avatarUrl?: string };
    if (body.avatarUrl) return body.avatarUrl;
  }
  return instructions.avatarUrl;
}

export async function fetchLanguageById(
  accessToken: string,
  id: string,
): Promise<Language | null> {
  const rows = await fetchAdminLanguages(accessToken);
  return rows.find((l) => l.id === id) ?? null;
}

export async function aiFillTranslations(
  accessToken: string,
  body: {
    targetLanguage: string;
    sourceLanguage?: string;
    context: LocalizationContext;
    keys?: string[];
    items?: Array<{ key: string; sourceText: string }>;
  },
): Promise<{
  items: Array<{ key: string; translatedText: string }>;
  provider: string;
}> {
  return authenticatedProApiFetch("/localization/ai/fill", accessToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface LocalizationHealthReport {
  generatedAt: string | null;
  computedAt: string;
  catalogSource: "url" | "file" | "none";
  totals: {
    localizedKeyCount: number;
    unlocalizedCount: number;
    orphanedContentKeyCount?: number;
    total: number;
    fullyTranslatedCount: number;
    partiallyTranslatedCount: number;
  };
  languages: Array<{
    code: string;
    name: string;
    flagUrl: string | null;
    translatedCount: number;
    untranslatedCount: number;
    unlocalizedCount: number;
    orphanedCount?: number;
  }>;
}

export async function fetchLocalizationHealth(
  accessToken: string,
  opts?: { fresh?: boolean },
): Promise<LocalizationHealthReport> {
  const qs = opts?.fresh ? "?fresh=1" : "";
  return authenticatedProApiFetch<LocalizationHealthReport>(
    `/localization/health${qs}`,
    accessToken,
    { method: "GET" },
  );
}

export interface ContentOrphansPayload {
  keys: string[];
  count: number;
  languagesUpdated?: number;
}

export async function fetchContentOrphans(
  accessToken: string,
): Promise<ContentOrphansPayload> {
  return authenticatedProApiFetch<ContentOrphansPayload>(
    "/localization/content-orphans",
    accessToken,
    { method: "GET" },
  );
}

export async function deleteContentOrphans(
  accessToken: string,
): Promise<ContentOrphansPayload> {
  return authenticatedProApiFetch<ContentOrphansPayload>(
    "/localization/content-orphans",
    accessToken,
    { method: "DELETE" },
  );
}

export interface LocalizationCatalogPayload {
  generatedAt: string | null;
  gitSha?: string | null;
  usedTKeys: string[];
  /** English defaults for used keys (from BOOTSTRAP_EN / seed at extract time). */
  defaultMessages?: Record<string, string>;
  unlocalized: Array<{
    file: string;
    line: number;
    snippet: string;
    suggestedKey?: string;
  }>;
  catalogSource: "url" | "file" | "none";
}

export async function fetchLocalizationCatalog(
  accessToken: string,
): Promise<LocalizationCatalogPayload> {
  return authenticatedProApiFetch<LocalizationCatalogPayload>(
    "/localization/catalog",
    accessToken,
    { method: "GET" },
  );
}
